import StrUtils from "../../utils/StrUtils";
import { useRef, useState } from "react";
import { Button, UncontrolledTooltip } from "reactstrap";
import { Tooltip, Whisper } from "rsuite";
import { useAtomValue } from "jotai";
import { IsMobileAtom } from "../../App";
import classnames from "classnames";

/**
 * DisplayRecord
 *
 * @param {*} mode - The mode of the record (e.g. r = read-only, rw = read-write)
 * @param {*} tableView - Whether or not the record is in a table view
 * @param {*} modifyTrigger - The trigger for opening the modify drawer (e.g. click, double-click)
 * @param {*} openModify - Function to open the modify drawer
 * @param {*} openView - Function to open the view drawer
 * @param {*} isSensitive - Whether or not the record is sensitive
 * @param {*} displayComponent - Function to render the display component
 */
interface DisplayRecordProps {
  mode: "r" | "rw";
  tableView: boolean;
  modifyTrigger: "click" | "doubleclick";
  openModify: () => void;
  openView?: () => void;
  isSensitive: boolean;
  displayComponent: (
    showEdit: boolean,
    displayInnerRef: React.RefObject<HTMLDivElement>
  ) => JSX.Element;
}

const DisplayRecord: React.FC<DisplayRecordProps> = ({
  mode,
  tableView,
  modifyTrigger,
  openModify,
  openView = undefined,
  isSensitive,
  displayComponent,
}) => {
  const mobileScreen = useAtomValue(IsMobileAtom);

  const [showEdit, setShowEdit] = useState(false);
  const [showSensitive, setShowSensitive] = useState(false);
  const [uniqueID] = useState(StrUtils.uuidv4());

  const mouseIn = (inside: boolean) => {
    setShowEdit(inside);
  };

  const toggleSensitiveField = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setShowSensitive(!showSensitive);
  };

  const displayRef = useRef<HTMLDivElement>(null);
  const displayInnerRef = useRef<HTMLDivElement>(null);

  return (
    <div
      style={{ position: "relative" }}
      className={classnames(
        { "cursor-pointer": mode === "rw" },
        { "pr-2 py-1": tableView },
        { "py-2": !tableView },
        {
          "d-flex flex-row-reverse align-items-center justify-content-between":
            mobileScreen,
        }
      )}
      onMouseOver={mobileScreen ? undefined : () => mouseIn(true)}
      onMouseLeave={mobileScreen ? undefined : () => mouseIn(false)}
      onDoubleClick={(e) => {
        if (modifyTrigger === "doubleclick") {
          if (
            e.target !== displayInnerRef?.current &&
            e.target !== displayRef.current &&
            !displayInnerRef?.current?.contains(e.target as Node) &&
            !displayRef?.current?.contains(e.target as Node)
          ) {
            return;
          }

          if (mode && mode === "rw") {
            if (openView) return openView();
            openModify();
          }
        }
      }}
      onClick={(e) => {
        if (modifyTrigger === "click") {
          if (
            e.target !== displayInnerRef?.current &&
            e.target !== displayRef.current &&
            !displayInnerRef?.current?.contains(e.target as Node) &&
            !displayRef?.current?.contains(e.target as Node)
          ) {
            return;
          }

          if (mode && mode === "rw") {
            if (openView) return openView();
            openModify();
          }
        }
      }}
      ref={displayRef}
    >
      {isSensitive && (showEdit || mobileScreen) && (
        <>
          <div
            style={{ position: "absolute", right: "25px" }}
            className="text-light"
            id={`tip${uniqueID}`}
            onClick={toggleSensitiveField}
          >
            <i className={`mdi mdi-${showSensitive ? "eye-off" : "eye"}`}></i>
          </div>
          <UncontrolledTooltip
            delay={0}
            placement="top"
            target={`tip${uniqueID}`}
          >
            Toggle Sensitive Field
          </UncontrolledTooltip>
        </>
      )}
      {mode === "rw" && (showEdit || mobileScreen) && (
        <>
          {mobileScreen ? (
            <i
              style={{
                borderRadius: 5,
                right: 2,
                paddingLeft: 6,
                color: "var(--medium)",
                fontSize: "14px",
                opacity: 0,
              }}
              className="mdi mdi-pencil cursor-pointer bg-white d-flex justify-content-center align-items-center"
            ></i>
          ) : (
            <Button
              size="sm"
              outline
              color="secondary"
              className="btn-icon-only"
              style={{
                position: "absolute",
                right: "0px",
                top: "50%",
                height: 22,
                width: 22,
                zIndex: 1,
                transform: "translateY(-50%)",
              }}
              onClick={(e) => {
                e.stopPropagation();

                if (mode && mode === "rw") {
                  openModify();
                }
              }}
            >
              <i className="mdi mdi-pencil" style={{ fontSize: 17 }}></i>
            </Button>
          )}
        </>
      )}
      {mode === "r" && (showEdit || mobileScreen) && (
        <>
          <Whisper
            placement="auto"
            speaker={<Tooltip>This field is read only.</Tooltip>}
            trigger="hover"
          >
            <i
              style={{
                position: mobileScreen ? undefined : "absolute",
                borderRadius: 5,
                right: 2,
                padding: mobileScreen ? "0 0 0 6px" : "0 3px",
                color: "var(--medium)",
                fontSize: mobileScreen ? "14px" : "",
              }}
              className="mdi mdi-eye cursor-pointer bg-white d-flex justify-content-center align-items-center"
            ></i>
          </Whisper>
        </>
      )}
      {isSensitive && !showSensitive
        ? "******"
        : typeof displayComponent === "function" &&
          displayComponent(showEdit, displayInnerRef)}
    </div>
  );
};

export default DisplayRecord;
